import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { useToast } from "@/hooks/use-toast";
import { securityApi } from "@/services/security.service";
import { setToken } from "@/lib/api-client-v2";

export function SecuritySettingsPanel() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data: status, isLoading: stLoading } = useQuery({
    queryKey: ["security", "status"],
    queryFn: () => securityApi.status(),
  });
  const { data: me, isLoading: meLoading } = useQuery({
    queryKey: ["security", "me"],
    queryFn: () => securityApi.me(),
  });

  const [setupOpen, setSetupOpen] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState("");
  const [recovery, setRecovery] = useState<string[] | null>(null);
  const [disable2faOpen, setDisable2faOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");
  const [pinDialog, setPinDialog] = useState<"setup" | "change" | "disable" | null>(null);
  const [pinValue, setPinValue] = useState("");
  const [oldPin, setOldPin] = useState("");
  const [pinPassword, setPinPassword] = useState("");

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["security"] });
  };

  const start2fa = useMutation({
    mutationFn: () => securityApi.setup2fa(),
    onSuccess: (data) => {
      setQr(data.qr_code_data_url);
      setRecovery(null);
      setTotpCode("");
      setSetupOpen(true);
    },
    onError: (e: Error) =>
      toast({ title: "Не удалось начать настройку 2FA", description: e.message, variant: "destructive" }),
  });

  const confirm2fa = useMutation({
    mutationFn: () => securityApi.verify2fa(totpCode),
    onSuccess: (data) => {
      if (data.access_token) setToken(data.access_token);
      setRecovery(data.recovery_codes);
      setQr(null);
      invalidate();
      toast({ title: "2FA включена", description: "Сохраните резервные коды в надёжном месте" });
    },
    onError: (e: Error) =>
      toast({ title: "Неверный код", description: e.message, variant: "destructive" }),
  });

  const disable2fa = useMutation({
    mutationFn: () => securityApi.disable2fa({ password: disablePassword || undefined }),
    onSuccess: () => {
      setDisable2faOpen(false);
      setDisablePassword("");
      invalidate();
      toast({ title: "2FA отключена" });
    },
    onError: (e: Error) =>
      toast({ title: "Не удалось отключить 2FA", description: e.message, variant: "destructive" }),
  });

  const setupPin = useMutation({
    mutationFn: () => securityApi.setupPin(pinValue),
    onSuccess: () => {
      setPinDialog(null);
      setPinValue("");
      invalidate();
      toast({ title: "PIN установлен" });
    },
    onError: (e: Error) =>
      toast({ title: "Ошибка PIN", description: e.message, variant: "destructive" }),
  });

  const changePin = useMutation({
    mutationFn: () => securityApi.changePin({ old_pin: oldPin, new_pin: pinValue }),
    onSuccess: () => {
      setPinDialog(null);
      setPinValue("");
      setOldPin("");
      invalidate();
      toast({ title: "PIN изменён" });
    },
    onError: (e: Error) =>
      toast({ title: "Ошибка смены PIN", description: e.message, variant: "destructive" }),
  });

  const disablePin = useMutation({
    mutationFn: () => securityApi.disablePin({ password: pinPassword || undefined }),
    onSuccess: () => {
      setPinDialog(null);
      setPinPassword("");
      invalidate();
      toast({ title: "PIN-блокировка отключена" });
    },
    onError: (e: Error) =>
      toast({ title: "Ошибка", description: e.message, variant: "destructive" }),
  });

  if (stLoading || meLoading) {
    return <p className="text-sm text-muted-foreground">Загрузка настроек безопасности…</p>;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          <CardTitle className="text-lg">Безопасность</CardTitle>
          <SectionHelpTooltip label="Справка: безопасность">
            <p className="font-medium">Безопасность аккаунта</p>
            <p>Двухфакторная аутентификация (TOTP) и блокировка экрана по PIN.</p>
            <ul className="list-disc space-y-1 pl-4">
              <li>2FA: QR → код из приложения → сохраните резервные коды.</li>
              <li>PIN: разблокировка после бездействия; токен разблокировки только в памяти.</li>
              <li>«Забыл PIN» — полный выход и повторный вход.</li>
            </ul>
          </SectionHelpTooltip>
        </div>
        <CardDescription>2FA и блокировка экрана по политике администратора</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <section className="space-y-2">
          <h3 className="text-sm font-medium">Двухфакторная аутентификация</h3>
          {!status?.two_factor_module_enabled ? (
            <p className="text-sm text-muted-foreground">
              Модуль 2FA выключен администратором системы.
            </p>
          ) : me?.two_factor_enabled ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-green-700 dark:text-green-400">Включена</span>
              <Button variant="outline" size="sm" onClick={() => setDisable2faOpen(true)}>
                Отключить
              </Button>
            </div>
          ) : (
            <Button size="sm" onClick={() => start2fa.mutate()} disabled={start2fa.isPending}>
              Включить 2FA
            </Button>
          )}
        </section>

        <section className="space-y-2">
          <h3 className="text-sm font-medium">Блокировка экрана (PIN)</h3>
          {!status?.screen_lock_module_enabled ? (
            <p className="text-sm text-muted-foreground">
              Модуль блокировки экрана выключен администратором системы.
            </p>
          ) : me?.screen_lock_enabled ? (
            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-green-700 dark:text-green-400 self-center">Включена</span>
              <Button variant="outline" size="sm" onClick={() => setPinDialog("change")}>
                Сменить PIN
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPinDialog("disable")}>
                Отключить
              </Button>
            </div>
          ) : (
            <Button size="sm" onClick={() => setPinDialog("setup")}>
              Установить PIN
            </Button>
          )}
          {me?.effective_idle_timeout_seconds ? (
            <p className="text-xs text-muted-foreground">
              Блокировка после бездействия: {me.effective_idle_timeout_seconds} с
            </p>
          ) : null}
        </section>
      </CardContent>

      <Dialog
        open={setupOpen}
        onOpenChange={(o) => {
          setSetupOpen(o);
          if (!o) {
            setQr(null);
            setRecovery(null);
            setTotpCode("");
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {recovery ? "Сохраните резервные коды" : "Настройка 2FA"}
            </DialogTitle>
            <DialogDescription>
              {recovery
                ? "Коды показываются один раз. Без них восстановление доступа будет сложнее."
                : "Отсканируйте QR в приложении-аутентификаторе и введите 6-значный код."}
            </DialogDescription>
          </DialogHeader>
          {qr ? (
            <div className="flex flex-col items-center gap-3">
              <img src={qr} alt="QR-код для TOTP" className="h-48 w-48" />
              <div className="w-full space-y-2">
                <Label htmlFor="totp">Код из приложения</Label>
                <Input
                  id="totp"
                  inputMode="numeric"
                  maxLength={6}
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                />
              </div>
              <Button
                className="w-full"
                disabled={totpCode.length !== 6 || confirm2fa.isPending}
                onClick={() => confirm2fa.mutate()}
              >
                Подтвердить
              </Button>
            </div>
          ) : null}
          {recovery ? (
            <div className="space-y-2">
              <ul className="grid grid-cols-2 gap-1 font-mono text-sm">
                {recovery.map((c) => (
                  <li key={c} className="rounded bg-muted px-2 py-1">
                    {c}
                  </li>
                ))}
              </ul>
              <DialogFooter>
                <Button onClick={() => setSetupOpen(false)}>Я сохранил коды</Button>
              </DialogFooter>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <AlertDialog open={disable2faOpen} onOpenChange={setDisable2faOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Отключить 2FA?</AlertDialogTitle>
            <AlertDialogDescription>
              Потребуется пароль. Резервные коды будут аннулированы.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="pwd">Текущий пароль</Label>
            <Input
              id="pwd"
              type="password"
              value={disablePassword}
              onChange={(e) => setDisablePassword(e.target.value)}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground"
              onClick={(e) => {
                e.preventDefault();
                disable2fa.mutate();
              }}
            >
              Отключить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={pinDialog !== null} onOpenChange={(o) => !o && setPinDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pinDialog === "setup"
                ? "Установка PIN"
                : pinDialog === "change"
                  ? "Смена PIN"
                  : "Отключение PIN"}
            </DialogTitle>
            <DialogDescription>
              {pinDialog === "disable"
                ? "Подтвердите паролем"
                : `PIN: ${status?.pin_min_length ?? 6}–${status?.pin_max_length ?? 6} цифр`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {pinDialog === "change" ? (
              <div className="space-y-1">
                <Label>Старый PIN</Label>
                <Input
                  type="password"
                  inputMode="numeric"
                  value={oldPin}
                  onChange={(e) => setOldPin(e.target.value.replace(/\D/g, ""))}
                />
              </div>
            ) : null}
            {pinDialog === "setup" || pinDialog === "change" ? (
              <div className="space-y-1">
                <Label>Новый PIN</Label>
                <Input
                  type="password"
                  inputMode="numeric"
                  value={pinValue}
                  onChange={(e) => setPinValue(e.target.value.replace(/\D/g, ""))}
                />
              </div>
            ) : null}
            {pinDialog === "disable" ? (
              <div className="space-y-1">
                <Label>Пароль</Label>
                <Input
                  type="password"
                  value={pinPassword}
                  onChange={(e) => setPinPassword(e.target.value)}
                />
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPinDialog(null)}>
              Отмена
            </Button>
            <Button
              onClick={() => {
                if (pinDialog === "setup") setupPin.mutate();
                else if (pinDialog === "change") changePin.mutate();
                else disablePin.mutate();
              }}
            >
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
